import React, { Fragment, ReactNode, useMemo, useState } from "react";
import classNames from "classnames";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import Button from "./ui-lib/buttons/Button";

type WizardProps = {
  children: ReactNode;
  className?: string;
  canActivateNextStep?: (nextStep: number) => Promise<boolean>;
  onFinish?: () => void;
  allowSkipSteps?: boolean;
};

type StepProps = {
  children: ReactNode;
  isValid: boolean | (() => boolean);
  name: string;
};

type WizardComponent = React.FC<WizardProps> & {
  Step: React.FC<StepProps>;
};

const Wizard: WizardComponent = ({
  children,
  className,
  canActivateNextStep = () => Promise.resolve(true),
  onFinish = () => {},
  allowSkipSteps = true,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = React.Children.toArray(children).filter(
    (step) => React.isValidElement(step) && step.type === WizardStep,
  );
  const showNavigation = useMemo(() => steps.length > 1, [steps]);

  const onActivateNextStep = () => {
    onActivateStep(activeStep + 1);
  };

  const onActivateStep = async (step: number) => {
    const canActivate = await canActivateNextStep(step);
    if (canActivate) {
      if (step === steps.length) {
        onFinish();
      } else {
        setActiveStep(step);
      }
    }
  };

  const onActivatePreviousStep = () => {
    setActiveStep((step) => step - 1);
  };

  const canActivate = (stepNumber: number) => {
    if (stepNumber === 0 || stepNumber < activeStep) return true;
    if (stepNumber > activeStep + 1 && !allowSkipSteps) return false;

    for (let i = activeStep; i < stepNumber; i++) {
      const step = steps[i];

      if (
        !React.isValidElement(step) ||
        step.type !== WizardStep ||
        !(typeof (step.props as StepProps)?.isValid === "function"
          ? ((step.props as StepProps).isValid as () => boolean)()
          : (step.props as StepProps)?.isValid)
      ) {
        return false;
      }

      return true;
    }
  };

  const getActiveWizardStep = () => {
    const step = steps[activeStep];
    if (React.isValidElement(step) && step.type === WizardStep) {
      return React.isValidElement(step) ? (step.props as StepProps) : undefined;
    }

    return undefined;
  };

  return (
    <div className={classNames("flex h-full flex-col", className)}>
      {showNavigation && (
        <>
          {/* Stepper */}
          <div className="mb-4 ml-auto mr-auto hidden items-center space-x-2 md:flex">
            <button
              onClick={onActivatePreviousStep}
              disabled={activeStep === 0}
              type="button"
              data-cy="wizard-back"
            >
              <ChevronLeftIcon
                className={classNames(
                  "h-10 w-10",
                  activeStep === 0
                    ? "text-th-primary-500"
                    : "text-th-primary-900",
                )}
              ></ChevronLeftIcon>
            </button>
            {steps.map((step, i) => (
              <Fragment key={`wizard_step_${i}`}>
                <div
                  className={classNames(
                    "rounded-full font-bold text-white",
                    "flex h-10 w-10 items-center justify-center",
                    i === activeStep
                      ? "bg-th-primary-900"
                      : "bg-th-primary-500",
                    canActivate(i) && "cursor-pointer hover:bg-th-primary-900",
                  )}
                  onClick={() => canActivate(i) && onActivateStep(i)}
                  data-cy={`wizard-step-button-${i}`}
                >
                  <span className="text-center">{i + 1}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className="h-1 w-7 bg-th-primary-900"></div>
                )}
              </Fragment>
            ))}
            <button
              onClick={onActivateNextStep}
              disabled={
                activeStep >= steps.length || !canActivate(activeStep + 1)
              }
              type="button"
              data-cy="wizard-next"
            >
              <ChevronRightIcon
                className={classNames(
                  "h-10 w-10",
                  activeStep >= steps.length || !canActivate(activeStep + 1)
                    ? "text-th-primary-500"
                    : "text-th-primary-900",
                )}
              />
            </button>
          </div>
          {/* Progress Circle */}
          <div className="mb-4 md:hidden">
            <span className="font-bold text-th-primary-900">
              Schritt {activeStep + 1} von {steps.length}
            </span>
            <h2>{getActiveWizardStep()?.name}</h2>
          </div>
        </>
      )}
      {/* Steps */}
      <div className="flex-grow">{steps[activeStep]}</div>
      {/* Bottom Navigation */}
      {showNavigation && (
        <div className="mt-4 flex justify-end space-x-2 md:hidden">
          <Button
            className="min-w-[100px]"
            onClick={onActivatePreviousStep}
            disabled={activeStep === 0}
            type="button"
            data-cy="wizard-back-mob"
          >
            Zurück
          </Button>
          <Button
            className="min-w-[100px]"
            onClick={onActivateNextStep}
            disabled={
              activeStep >= steps.length || !canActivate(activeStep + 1)
            }
            type="button"
            data-cy="wizard-next-mob"
          >
            {activeStep === steps.length - 1 ? "Fertig" : "Weiter"}
          </Button>
        </div>
      )}
    </div>
  );
};

const WizardStep: React.FC<StepProps> = ({ children }) => {
  return <>{children}</>;
};

Wizard.Step = WizardStep;

export default Wizard;
